import { useState } from 'react';
import { ApiRequest } from '../types';

export function ApiTester() {
  const [url, setUrl] = useState('');
  const [method, setMethod] = useState('POST');
  const [headers, setHeaders] = useState<Record<string, string>>({
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Authorization': 'Bearer '
  });
  const [headersError, setHeadersError] = useState<string | null>(null);
  const [rawHeaders, setRawHeaders] = useState(JSON.stringify(headers, null, 2));
  const [body, setBody] = useState('');
  const [response, setResponse] = useState<ApiRequest | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: method !== 'GET' ? body : undefined,
      });

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (err) {
          data = null;
        }
      } else {
        data = null;
      }

      setResponse({
        id: Date.now().toString(),
        url,
        method,
        headers,
        body: method !== 'GET' ? JSON.parse(body) : undefined,
        response: {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          data,
        },
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="api-section p-4 h-full flex flex-col">
      <div className="terminal-header">
        <h3 className="text-sm font-mono" style={{ color: 'var(--theme-color)' }}>API Tester</h3>
        <div className="flex space-x-2">
          <span className="api-badge">REST</span>
          <span className="api-badge">Live</span>
        </div>
      </div>
      <div className="text-hacker-text-muted text-xs mt-1">
        <p>Test your APIs with different HTTP methods and payloads.</p>
      </div>

      <div className="mt-3 flex-1 flex flex-col min-h-0">
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-full sm:flex-1">
              <label htmlFor="url" className="hacker-label">
                API URL
              </label>
              <div className="flex items-center">
                <span className="terminal-prompt mr-2">curl</span>
                <input
                  type="url"
                  name="url"
                  id="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="api-input flex-1 h-[2.5rem]"
                  placeholder="https://api.example.com/endpoint"
                  required
                />
              </div>
            </div>

            <div className="w-full sm:w-[200px]">
              <label htmlFor="method" className="hacker-label">
                HTTP Method
              </label>
              <div className="flex items-center">
                <span className="terminal-prompt mr-2">-X</span>
                <select
                  id="method"
                  name="method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className="api-select flex-1 h-[2.5rem]"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </div>
            </div>
          </div>

          <div className="mt-4 flex-1 flex flex-col min-h-[200px]">
            <label htmlFor="headers" className="hacker-label">
              Headers
            </label>
            <div className="flex flex-1 min-h-0">
              <span className="terminal-prompt mr-2">-H</span>
              <textarea
                id="headers"
                name="headers"
                value={rawHeaders}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setRawHeaders(newValue);
                  try {
                    const newHeaders = JSON.parse(newValue);
                    if (typeof newHeaders === 'object' && newHeaders !== null) {
                      setHeaders(newHeaders);
                      setHeadersError(null);
                    } else {
                      setHeadersError('Headers must be a valid JSON object');
                    }
                  } catch (err) {
                    if (err instanceof SyntaxError) {
                      setHeadersError(`Invalid JSON: ${err.message}`);
                    } else {
                      setHeadersError('Invalid JSON format');
                    }
                  }
                }}
                className="api-textarea flex-1 resize-none h-full"
                placeholder='{"Content-Type": "application/json"}'
              />
            </div>
            {headersError && (
              <div className="mt-1 text-xs text-red-500 font-mono">
                {headersError}
              </div>
            )}
          </div>

          {method !== 'GET' && (
            <div className="mt-4 flex-1 flex flex-col min-h-[200px]">
              <label htmlFor="body" className="hacker-label">
                Request Body
              </label>
              <div className="flex flex-1 min-h-0">
                <span className="terminal-prompt mr-2">-d</span>
                <textarea
                  id="body"
                  name="body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className="api-textarea flex-1 resize-none h-full"
                  placeholder='{"key": "value"}'
                />
              </div>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="api-button h-[2.5rem]"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-neon-api" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                'Send Request'
              )}
            </button>
          </div>

          {error && (
            <div className="mt-4 hacker-error border-neon-api text-neon-api">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-2">
                  <h3 className="text-xs font-mono">Error</h3>
                  <div className="mt-1 text-xs opacity-80">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>

        {response && (
          <div className="mt-4 flex-1 flex flex-col min-h-[12rem]">
            <label className="hacker-label">
              Response
            </label>
            <div className="flex flex-1 min-h-0">
              <span className="terminal-prompt mr-2" style={{ color: 'var(--theme-color)' }}>$</span>
              <div className="response-section flex-1 min-h-0">
                <div className="response-header">
                  <div></div>
                  <span className={`method-badge ${method.toLowerCase()}`}>
                    {response.response.status} {response.response.statusText}
                  </span>
                </div>
                <div className="response-content overflow-auto">
                  <pre className="text-sm">
                    {JSON.stringify(response, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}